import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
/**
 * Request Memo Modal
 * Uses gold theme from BaseModal system
 */
import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuthStore } from '../stores/authStore';
import { useTour } from '../contexts/TourContext';
import { apiClient } from '../utils/apiClient';
import { MemoHelpers } from '../utils/memoHelpers';
import { notify } from '../utils/notifications';
import { BaseModal, Input as BaseInput, Textarea as BaseTextarea, FormGroup as BaseFormGroup, FormLabel, PrimaryButton, SecondaryButton, } from './shared/BaseModal';
const DiamondSummary = styled.div `
  background: var(--bg-tertiary);
  padding: 20px;
  margin-bottom: 24px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, var(--accent-gold), var(--accent-gold-light));
  }

  h3 {
    color: var(--text-primary);
    font-family: 'Playfair Display', serif;
    font-size: 18px;
    margin: 0 0 12px 0;
  }

  .details {
    color: var(--text-secondary);
    font-size: 14px;
    line-height: 1.6;
  }
`;
const PriceSection = styled.div `
  background: ${props => props.$hasMarkup ? 'rgba(184, 134, 11, 0.06)' : 'var(--bg-tertiary)'};
  border: 1px solid ${props => props.$hasMarkup ? 'var(--accent-gold)' : 'var(--border-color)'};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;

  .price-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;

    &:last-child {
      margin-bottom: 0;
      padding-top: 12px;
      border-top: 1px solid var(--border-color);
      font-weight: 700;
    }

    .label {
      color: var(--text-secondary);
      font-size: 14px;
    }

    .value {
      color: var(--accent-gold);
      font-size: 16px;
      font-weight: 600;
      font-family: 'Playfair Display', serif;
    }
  }

  .markup-notice {
    background: rgba(184, 134, 11, 0.08);
    padding: 10px 14px;
    margin-top: 12px;
    font-size: 12px;
    color: var(--accent-gold-dark);
    border-left: 3px solid var(--accent-gold);
    border-radius: 0 4px 4px 0;
  }
`;
const Select = styled.select `
  width: 100%;
  padding: 14px 16px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:focus {
    outline: none;
    border-color: var(--accent-gold);
    box-shadow: 0 0 0 3px var(--accent-gold-bg);
  }

  option {
    background: var(--bg-secondary);
    color: var(--text-primary);
  }
`;
const Input = styled(BaseInput) `
  padding: 14px 16px;
`;
const Textarea = styled(BaseTextarea) `
  min-height: 100px;
`;
const FormGroup = styled(BaseFormGroup) `
  margin-bottom: 16px;
`;
const ButtonGroup = styled.div `
  display: flex;
  gap: 12px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid var(--border-color);

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;
export const RequestMemoModal = ({ diamond, isOpen, onClose, onSuccess }) => {
    const { user } = useAuthStore();
    const { currentTour } = useTour();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    // Block submit during memo request tutorial
    const isMemoTutorialActive = currentTour?.tourId === 'memo-request-tour';
    // Scroll modal to top when opened during tour to fix spotlight positioning
    useEffect(() => {
        if (isOpen && isMemoTutorialActive) {
            // Scroll the modal container to top
            setTimeout(() => {
                const modalElement = document.querySelector('#tour-memo-modal');
                if (modalElement && modalElement.parentElement) {
                    // Scroll parent container (the ModalContainer) to top
                    modalElement.parentElement.scrollTop = 0;
                }
            }, 100);
        }
    }, [isOpen, isMemoTutorialActive]);
    const [formData, setFormData] = useState({
        clientName: '',
        clientContact: '',
        purpose: 'viewing',
        dealerNotes: '',
        daysUntilDue: 7
    });
    const handleInputChange = (e) => {
        setFormData(prev => ({
            ...prev,
            [e.target.name]: e.target.value
        }));
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        // Check selling permission
        if (user?.profile?.customerInfo?.sellingPermission === 'denied') {
            setError('Your account does not have permission to request memos. Please contact an administrator.');
            notify.error('Permission Denied', 'Your account does not have permission to request memos');
            return;
        }
        // Validation
        if (!formData.clientName.trim()) {
            setError('Client name is required');
            return;
        }
        try {
            setIsSubmitting(true);
            const basePrice = diamond.priceUSD || diamond.totalPrice || 0;
            const memoPrice = user ? MemoHelpers.calculateMemoPrice(basePrice, user.roleLevel) : basePrice;
            const caratWeight = diamond.caratWeight || parseFloat(diamond.carat || '0') || 0;
            const response = await apiClient.post('memos/create', {
                diamondId: diamond.id,
                diamondDetails: {
                    shape: diamond.shape,
                    caratWeight: caratWeight,
                    color: diamond.color,
                    clarity: diamond.clarity,
                    cut: diamond.cut,
                    lab: diamond.lab || diamond.certification || 'Unknown',
                    certificateNumber: diamond.certificateNumber,
                    stockNumber: diamond.stockNumber,
                    priceUSD: memoPrice
                },
                clientName: formData.clientName,
                clientContact: formData.clientContact,
                purpose: formData.purpose,
                dealerNotes: formData.dealerNotes
            });
            if (notify.handleApiResponse(response, {
                successTitle: 'Memo Request Submitted',
                successMessage: 'Your memo request has been submitted and is pending admin approval',
                errorTitle: 'Request Failed'
            })) {
                onSuccess?.();
                onClose();
            }
        }
        catch (error) {
            console.error('Memo request failed:', error);
            notify.error('Request Failed', 'An unexpected error occurred while submitting your memo request');
        }
        finally {
            setIsSubmitting(false);
        }
    };
    const basePrice = diamond.priceUSD || diamond.totalPrice || 0;
    const memoPrice = user ? MemoHelpers.calculateMemoPrice(basePrice, user.roleLevel) : basePrice;
    const markupPercent = user ? MemoHelpers.getMemoMarkupPercent(user.roleLevel) : 0;
    const hasMarkup = markupPercent > 0;
    const caratWeight = diamond.caratWeight || parseFloat(diamond.carat || '0') || 0;
    const lab = diamond.lab || diamond.certification || 'Unknown';
    return (_jsx(BaseModal, { isOpen: isOpen, onClose: onClose, title: "Request Diamond Memo", titleAccent: "Memo", size: "lg", children: _jsxs("div", { id: "tour-memo-modal", children: [_jsxs(DiamondSummary, { children: [_jsxs("h3", { children: [caratWeight, "ct ", diamond.shape, " Diamond"] }), _jsxs("div", { className: "details", children: [diamond.color, " / ", diamond.clarity, " / ", diamond.cut, _jsx("br", {}), lab, " ", diamond.certificateNumber && `• Cert #${diamond.certificateNumber}`, _jsx("br", {}), diamond.stockNumber && `Stock #${diamond.stockNumber}`] })] }), _jsxs(PriceSection, { "$hasMarkup": hasMarkup, children: [_jsxs("div", { className: "price-row", children: [_jsx("span", { className: "label", children: "Base Price:" }), _jsxs("span", { className: "value", children: ["$", basePrice.toLocaleString()] })] }), _jsxs("div", { className: "price-row", children: [_jsxs("span", { className: "label", children: ["Service Fee (", markupPercent, "%):"] }), _jsx("span", { className: "value", children: hasMarkup ? `+$${(memoPrice - basePrice).toLocaleString()}` : '$0' })] }), _jsxs("div", { className: "price-row", children: [_jsx("span", { className: "label", children: "Total Memo Price:" }), _jsxs("span", { className: "value", children: ["$", memoPrice.toLocaleString()] })] }), hasMarkup && (_jsx("div", { className: "markup-notice", children: "Basic Dealers pay a 5% service fee on memo requests. Upgrade to Verified Dealer for standard pricing." }))] }), error && (_jsx("div", { style: {
                        color: '#B91C1C',
                        padding: '12px 14px',
                        border: '1px solid rgba(220, 38, 38, 0.2)',
                        marginBottom: '16px',
                        backgroundColor: 'rgba(220, 38, 38, 0.06)',
                        fontSize: '14px',
                        borderRadius: '6px'
                    }, children: error })), _jsxs("form", { onSubmit: handleSubmit, children: [_jsxs(FormGroup, { children: [_jsx(FormLabel, { htmlFor: "clientName", children: "Client Name *" }), _jsx(Input, { id: "clientName", name: "clientName", value: formData.clientName, onChange: handleInputChange, required: true, placeholder: "Enter client's full name" })] }), _jsxs(FormGroup, { children: [_jsx(FormLabel, { htmlFor: "clientContact", children: "Client Contact" }), _jsx(Input, { id: "clientContact", name: "clientContact", value: formData.clientContact, onChange: handleInputChange, placeholder: "Email or phone number" })] }), _jsxs(FormGroup, { children: [_jsx(FormLabel, { htmlFor: "purpose", children: "Purpose" }), _jsxs(Select, { id: "purpose", name: "purpose", value: formData.purpose, onChange: handleInputChange, children: [_jsx("option", { value: "viewing", children: "Client Viewing" }), _jsx("option", { value: "appraisal", children: "Appraisal" }), _jsx("option", { value: "consideration", children: "Purchase Consideration" })] })] }), _jsxs(FormGroup, { children: [_jsx(FormLabel, { htmlFor: "daysUntilDue", children: "Memo Duration" }), _jsxs(Select, { id: "daysUntilDue", name: "daysUntilDue", value: formData.daysUntilDue, onChange: handleInputChange, children: [_jsx("option", { value: "7", children: "7 days" }), _jsx("option", { value: "10", children: "10 days" }), _jsx("option", { value: "14", children: "14 days" })] })] }), _jsxs(FormGroup, { children: [_jsx(FormLabel, { htmlFor: "dealerNotes", children: "Notes (Optional)" }), _jsx(Textarea, { id: "dealerNotes", name: "dealerNotes", value: formData.dealerNotes, onChange: handleInputChange, placeholder: "Add any special notes or instructions..." })] }), _jsxs(ButtonGroup, { children: [_jsx(SecondaryButton, { type: "button", onClick: onClose, disabled: isSubmitting, style: { flex: 1 }, children: "Cancel" }), _jsx(PrimaryButton, { id: "tour-memo-submit", type: "submit", disabled: isSubmitting || isMemoTutorialActive, style: { flex: 1 }, title: isMemoTutorialActive ? 'Submit is disabled during tutorial - complete the tutorial to learn the full process!' : undefined, children: isSubmitting ? 'Submitting...' : 'Submit Memo Request' })] }), isMemoTutorialActive && (_jsx("div", { style: {
                                fontSize: '12px',
                                color: 'var(--accent-gold)',
                                marginTop: '12px',
                                textAlign: 'center',
                                padding: '10px 14px',
                                background: 'var(--accent-gold-bg)',
                                border: '1px solid rgba(184, 134, 11, 0.2)',
                                borderRadius: '6px'
                            }, children: "Submit is disabled during tutorial. Complete the tutorial to learn the process!" })), _jsx("div", { style: {
                                fontSize: '12px',
                                color: 'var(--text-tertiary)',
                                marginTop: '16px',
                                textAlign: 'center'
                            }, children: "Memo requests are subject to credit approval and account status" })] })] }) }));
};
export default RequestMemoModal;
